import type { HttpAgent } from "@icp-sdk/core/agent";
import { Principal } from "@icp-sdk/core/principal";
import { natToAmount } from "@motokomaker/ic-client";
import {
	fetchAccountTransactions,
	type WalletTxRow,
} from "@/lib/icrc-history";

export type ActivityCategory =
	| "transfer"
	| "vault"
	| "liquidation"
	| "savings"
	| "other";

export type ActivityStatus = "confirmed" | "pending" | "failed";

export type ActivityEntry = {
	id: string;
	timestampNs: bigint;
	category: ActivityCategory;
	type: string;
	asset: string;
	amount: number;
	amountE8: bigint | null;
	direction: "in" | "out" | "neutral";
	counterparty: string | null;
	/** Ledger-style origin (principal id, vault#N, protocol, …) */
	fromAccount: string | null;
	/** Ledger-style destination */
	toAccount: string | null;
	vaultId: bigint | null;
	txRef: string;
	status: ActivityStatus;
	source: "protocol_flow" | "usdm_index" | "mmk_index" | "client";
	detail: string;
};

export type TreasuryFlowLike = {
	id: bigint;
	timestamp: bigint;
	kind: Record<string, null> | { [k: string]: null };
	direction: { In: null } | { Out: null } | Record<string, null>;
	token: string;
	amountE8: bigint;
	vaultId: [] | [bigint];
	detail: string;
	initiator: string;
	fromAccount?: string;
	toAccount?: string;
};

function kindKey(kind: TreasuryFlowLike["kind"]): string {
	return Object.keys(kind as object)[0] ?? "Other";
}

function directionKey(
	d: TreasuryFlowLike["direction"],
): "in" | "out" | "neutral" {
	if ("In" in d) return "in";
	if ("Out" in d) return "out";
	return "neutral";
}

function mapFlowKind(kind: string): {
	category: ActivityCategory;
	type: string;
} {
	switch (kind) {
		case "CollateralLock":
			return { category: "vault", type: "CollateralDeposit" };
		case "CollateralRelease":
			return { category: "vault", type: "CollateralWithdraw" };
		case "UsdmMint":
			return { category: "vault", type: "Mint" };
		case "UsdmBurn":
			return { category: "vault", type: "Repay" };
		case "LiquidationProceeds":
			return { category: "liquidation", type: "LiquidationPay" };
		case "LiquidationPayout":
			return { category: "liquidation", type: "CollateralSeized" };
		case "TreasurySeizure":
			return { category: "liquidation", type: "TreasurySeizure" };
		case "AgentCommission":
			return { category: "liquidation", type: "AgentPay" };
		case "SavingsDeposit":
			return { category: "savings", type: "SavingsDeposit" };
		case "SavingsWithdraw":
			return { category: "savings", type: "SavingsWithdraw" };
		case "SurplusDebit":
			return { category: "savings", type: "InterestReward" };
		case "StabilityFee":
			return { category: "other", type: "StabilityFee" };
		case "MmkSale":
			return { category: "other", type: "MmkBuy" };
		case "BadDebtRecord":
			return { category: "other", type: "BadDebt" };
		case "BadDebtSettlement":
			return { category: "other", type: "BadDebtSettlement" };
		case "ParamChange":
			return { category: "other", type: "ParamChange" };
		case "PauseChange":
			return { category: "other", type: "PauseChange" };
		default:
			return { category: "other", type: kind };
	}
}

export function mapTreasuryFlowToActivity(e: TreasuryFlowLike): ActivityEntry {
	const k = kindKey(e.kind);
	const { category, type } = mapFlowKind(k);
	const dir = directionKey(e.direction);
	const vaultId = e.vaultId.length > 0 ? e.vaultId[0]! : null;
	const fromAccount =
		e.fromAccount && e.fromAccount.length > 0
			? e.fromAccount
			: dir === "out"
				? e.initiator
				: vaultId != null
					? `vault#${vaultId.toString()}`
					: "protocol";
	const toAccount =
		e.toAccount && e.toAccount.length > 0
			? e.toAccount
			: dir === "in"
				? e.initiator
				: vaultId != null
					? `vault#${vaultId.toString()}`
					: "protocol";
	return {
		id: `flow:${e.id.toString()}`,
		timestampNs: e.timestamp,
		category,
		type,
		asset: e.token,
		amount: natToAmount(e.amountE8),
		amountE8: e.amountE8,
		direction: dir,
		counterparty: vaultId != null ? `vault #${vaultId.toString()}` : "protocol",
		fromAccount,
		toAccount,
		vaultId,
		txRef: e.id.toString(),
		status: "confirmed",
		source: "protocol_flow",
		detail: e.detail,
	};
}

function mapIndexTx(
	row: WalletTxRow,
	source: "usdm_index" | "mmk_index",
): ActivityEntry {
	const typeMap: Record<string, string> = {
		transfer_in: "TransferIn",
		transfer_out: "TransferOut",
		mint: "LedgerMint",
		burn: "LedgerBurn",
		approve: "Approve",
		other: "Other",
	};
	const dir: ActivityEntry["direction"] =
		row.kind === "transfer_in" || row.kind === "mint"
			? "in"
			: row.kind === "transfer_out" || row.kind === "burn"
				? "out"
				: "neutral";
	return {
		id: `${source}:${row.id}`,
		timestampNs: row.timestampNs,
		category: "transfer",
		type: typeMap[row.kind] ?? "Other",
		asset: row.token,
		amount: row.amount,
		amountE8: row.amountE8,
		direction: dir,
		counterparty: row.counterparty,
		fromAccount: row.fromAccount ?? null,
		toAccount: row.toAccount ?? null,
		vaultId: null,
		txRef: row.blockIndex.toString(),
		status: "confirmed",
		source,
		detail: `${row.kind} block ${row.blockIndex.toString()}`,
	};
}

function lightDedupe(entries: ActivityEntry[]): ActivityEntry[] {
	const seen = new Set<string>();
	const out: ActivityEntry[] = [];
	for (const e of entries) {
		const bucket = `${e.timestampNs / 1_000_000_000n}:${e.asset}:${e.amountE8?.toString() ?? e.amount}:${e.direction}`;
		if (e.source === "protocol_flow") {
			seen.add(bucket);
			out.push(e);
			continue;
		}
		if (seen.has(bucket)) continue;
		seen.add(bucket);
		out.push(e);
	}
	return out;
}

export type ActivityFetchDeps = {
	agent?: HttpAgent | null;
	principal: Principal;
	protocolFlows: TreasuryFlowLike[];
	includeUsdmIndex?: boolean;
	includeMmkIndex?: boolean;
	indexMaxResults?: number;
};

export async function fetchUserActivity(
	deps: ActivityFetchDeps,
): Promise<ActivityEntry[]> {
	const fromFlows = deps.protocolFlows.map(mapTreasuryFlowToActivity);
	const indexRows: ActivityEntry[] = [];
	const indexLimit = deps.indexMaxResults ?? 15;

	if (deps.includeUsdmIndex && deps.agent) {
		try {
			const usdm = await fetchAccountTransactions(
				deps.agent,
				"USDM",
				deps.principal,
				indexLimit,
			);
			for (const r of usdm.rows) indexRows.push(mapIndexTx(r, "usdm_index"));
		} catch {
			/* optional */
		}
	}
	if (deps.includeMmkIndex && deps.agent) {
		try {
			const mmk = await fetchAccountTransactions(
				deps.agent,
				"MMK",
				deps.principal,
				indexLimit,
			);
			for (const r of mmk.rows) indexRows.push(mapIndexTx(r, "mmk_index"));
		} catch {
			/* optional */
		}
	}

	const merged = lightDedupe([...fromFlows, ...indexRows]);
	merged.sort((a, b) => {
		if (a.timestampNs === b.timestampNs) return 0;
		return a.timestampNs > b.timestampNs ? -1 : 1;
	});
	return merged;
}

export function formatActivityTime(timestampNs: bigint): string {
	const ms = Number(timestampNs / 1_000_000n);
	if (!Number.isFinite(ms) || ms <= 0) return "—";
	return new Date(ms).toLocaleString();
}

function dayStartNs(day: string): bigint | null {
	if (!day || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
	const t = new Date(`${day}T00:00:00`).getTime();
	if (!Number.isFinite(t)) return null;
	return BigInt(t) * 1_000_000n;
}

function dayEndNs(day: string): bigint | null {
	if (!day || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
	const t = new Date(`${day}T23:59:59.999`).getTime();
	if (!Number.isFinite(t)) return null;
	return BigInt(t) * 1_000_000n;
}

function assetMatches(entryAsset: string, filter: string): boolean {
	if (filter === "all") return true;
	const a = entryAsset.toUpperCase();
	const f = filter.toUpperCase();
	if (a === f) return true;
	if (f === "USDM" && (a === "USDM" || a.includes("USDM"))) return true;
	if (f === "MMK" && (a === "MMK" || a.includes("MMK"))) return true;
	if (f === "ICP" && (a === "ICP" || a.includes("ICP"))) return true;
	if (f === "CKBTC" && (a === "CKBTC" || a.includes("BTC"))) return true;
	if (f === "CKETH" && (a === "CKETH" || a.includes("ETH"))) return true;
	if (f === "CKUSDC" && (a === "CKUSDC" || a.includes("USDC"))) return true;
	return a.includes(f) || f.includes(a);
}

export function filterActivity(
	entries: ActivityEntry[],
	opts: {
		category?: ActivityCategory | "all";
		asset?: string | "all";
		fromDay?: string;
		toDay?: string;
	},
): ActivityEntry[] {
	const fromNs = opts.fromDay ? dayStartNs(opts.fromDay) : null;
	const toNs = opts.toDay ? dayEndNs(opts.toDay) : null;
	return entries.filter((e) => {
		if (
			opts.category &&
			opts.category !== "all" &&
			e.category !== opts.category
		) {
			return false;
		}
		if (
			opts.asset &&
			opts.asset !== "all" &&
			!assetMatches(e.asset, opts.asset)
		) {
			return false;
		}
		if (fromNs != null && e.timestampNs < fromNs) return false;
		if (toNs != null && e.timestampNs > toNs) return false;
		return true;
	});
}
